import { required, type Match, type Tournament } from "./model";

export interface Standing {
  teamId: string;
  wins: number;
  played: number;
  sets: number;
  setsLost: number;
  points: number;
  pointsLost: number;
  opponentsWins: number;
  place: number;
  tied: boolean;
}

export interface StandingTie {
  key: string;
  label: string;
  place: number;
  teamIds: string[];
}

type Compare = (a: Standing, b: Standing) => number;
interface RankingRules {
  scope: string;
  label: string;
  compare: Compare;
  matches?: Match[];
  fallback?: Compare;
  ties?: StandingTie[];
  placeOffset?: number;
}

function groups(rows: Standing[], compare: Compare): Standing[][] {
  const result: Standing[][] = [];
  for (const row of rows.toSorted(compare)) {
    const last = result.at(-1);
    if (last && compare(required(last[0]), row) === 0) last.push(row);
    else result.push([row]);
  }
  return result;
}

function headToHead(rows: Standing[], matches: Match[]): Standing[][] {
  const wins = new Map(rows.map((row) => [row.teamId, 0]));
  for (const [i, a] of rows.entries()) {
    for (const b of rows.slice(i + 1)) {
      const meetings = matches.filter(
        (m) =>
          m.winnerId &&
          m.teams.includes(a.teamId) &&
          m.teams.includes(b.teamId),
      );
      if (!meetings.length) return [rows];
      for (const match of meetings) {
        const id = required(match.winnerId);
        wins.set(id, required(wins.get(id)) + 1);
      }
    }
  }
  return groups(
    rows,
    (a, b) => required(wins.get(b.teamId)) - required(wins.get(a.teamId)),
  );
}

export function rank(
  t: Tournament,
  input: Standing[],
  rules: RankingRules,
): Standing[] {
  const rows = input.map((row) => ({ ...row, tied: false }));
  const withdrawn = new Set(
    t.teams.filter((team) => team.withdrawn).map((team) => team.id),
  );
  const ranked: Standing[] = [];
  const append = (group: Standing[]) => {
    if (group.length === 1) {
      ranked.push(required(group[0]));
      return;
    }
    // Partition the whole tie before ordering it; pairwise head-to-head sorting breaks on A > B > C > A.
    const meetings =
      rules.matches?.filter((m) =>
        m.teams.every((id) => group.some((row) => row.teamId === id)),
      ) ?? [];
    const head = headToHead(group, meetings);
    if (head.length > 1) {
      head.forEach(append);
      return;
    }
    const fallback = rules.fallback && groups(group, rules.fallback);
    if (fallback && fallback.length > 1) {
      fallback.forEach(append);
      return;
    }
    const ordered = group.toSorted((a, b) => a.teamId.localeCompare(b.teamId));
    const key = JSON.stringify([
      rules.scope,
      ordered.map(
        ({
          teamId,
          wins,
          played,
          sets,
          setsLost,
          points,
          pointsLost,
          opponentsWins,
        }) => [
          teamId,
          wins,
          played,
          sets,
          setsLost,
          points,
          pointsLost,
          opponentsWins,
        ],
      ),
      meetings
        .map((m) => [m.id, m.revision, m.winnerId])
        .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
    ]);
    const decision = t.tieDecisions?.find((tie) => tie.key === key);
    const winner =
      decision && group.find((row) => row.teamId === decision.winnerId);
    if (winner) {
      ranked.push(winner);
      append(group.filter((row) => row !== winner));
      return;
    }
    rules.ties?.push({
      key,
      label: rules.label,
      place: (rules.placeOffset ?? 0) + ranked.length + 1,
      teamIds: group.map((row) => row.teamId),
    });
    group.sort(
      (a, b) =>
        required(t.teams.find((team) => team.id === a.teamId)).seed -
        required(t.teams.find((team) => team.id === b.teamId)).seed,
    );
    group.forEach((row) => {
      row.tied = true;
    });
    ranked.push(...group);
  };
  groups(
    rows.filter((row) => !withdrawn.has(row.teamId)),
    rules.compare,
  ).forEach(append);
  ranked.push(...rows.filter((row) => withdrawn.has(row.teamId)));
  ranked.forEach((row, i) => {
    row.place = i + 1;
  });
  return ranked;
}
